import type { ProgramSlideLayoutPatch, ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';

/** 슬라이드 블록 그룹 멤버 (캔버스 `SlideSelectionKind`와 동일 집합) */
export type SlideBlockGroupRef = {
  kind:
    | 'text'
    | 'image'
    | 'clipboardImage'
    | 'scanImage'
    | 'button'
    | 'shape'
    | 'polyline'
    | 'table'
    | 'video'
    | 'webpage'
    | 'quiz'
    | 'quizChoice';
  id: string;
};

export function slideBlockGroupRefKey(ref: Pick<SlideBlockGroupRef, 'kind' | 'id'>): string {
  return `${ref.kind}:${ref.id}`;
}

/** DB/JSON에서 읽은 `groupId` 정규화 */
export function parseSlideBlockGroupId(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  if (!s || s.length > 128) return undefined;
  if (!/^[\w:-]+$/.test(s)) return undefined;
  return s;
}

export function genSlideBlockGroupId(): string {
  try {
    const c = globalThis.crypto?.randomUUID?.();
    if (c) return `grp:${c}`;
  } catch {
    /* ignore */
  }
  return `grp:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
}

export function omitSlideBlockGroupId<T extends { groupId?: string }>(obj: T): T {
  if (obj.groupId == null || obj.groupId === '') return obj;
  const { groupId: _omit, ...rest } = obj;
  return rest as T;
}

export function getSlideBlockGroupId(layout: ProgramSlideLayoutV1, kind: string, id: string): string | undefined {
  if (kind === 'text') {
    return layout.boxes.find((b) => b.id === id)?.groupId;
  }
  if (kind === 'image') {
    return layout.images.find((im) => im.id === id)?.groupId;
  }
  if (kind === 'clipboardImage') {
    return (layout.clipboardImages ?? []).find((im) => im.id === id)?.groupId;
  }
  if (kind === 'scanImage') {
    return (layout.scanImages ?? []).find((im) => im.id === id)?.groupId;
  }
  if (kind === 'button') {
    return layout.buttons.find((b) => b.id === id)?.groupId;
  }
  if (kind === 'shape') {
    return (layout.shapes ?? []).find((s) => s.id === id)?.groupId;
  }
  if (kind === 'polyline') {
    return (layout.polylines ?? []).find((p) => p.id === id)?.groupId;
  }
  if (kind === 'table') {
    return (layout.tables ?? []).find((t) => t.id === id)?.groupId;
  }
  if (kind === 'video') {
    return layout.videos.find((v) => v.id === id)?.groupId;
  }
  if (kind === 'quiz') {
    return layout.quizzes.find((q) => q.id === id)?.groupId;
  }
  if (kind === 'quizChoice') {
    return layout.quizChoiceOptions.find((o) => o.id === id)?.groupId;
  }
  return undefined;
}

export function collectRefsWithGroupId(layout: ProgramSlideLayoutV1, groupId: string): SlideBlockGroupRef[] {
  const out: SlideBlockGroupRef[] = [];
  for (const b of layout.boxes) {
    if (b.groupId === groupId) out.push({ kind: 'text', id: b.id });
  }
  for (const im of layout.images) {
    if (im.groupId === groupId) out.push({ kind: 'image', id: im.id });
  }
  for (const im of layout.clipboardImages ?? []) {
    if (im.groupId === groupId) out.push({ kind: 'clipboardImage', id: im.id });
  }
  for (const im of layout.scanImages ?? []) {
    if (im.groupId === groupId) out.push({ kind: 'scanImage', id: im.id });
  }
  for (const btn of layout.buttons) {
    if (btn.groupId === groupId) out.push({ kind: 'button', id: btn.id });
  }
  for (const s of layout.shapes ?? []) {
    if (s.groupId === groupId) out.push({ kind: 'shape', id: s.id });
  }
  for (const pl of layout.polylines ?? []) {
    if (pl.groupId === groupId) out.push({ kind: 'polyline', id: pl.id });
  }
  for (const tbl of layout.tables ?? []) {
    if (tbl.groupId === groupId) out.push({ kind: 'table', id: tbl.id });
  }
  for (const v of layout.videos) {
    if (v.groupId === groupId) out.push({ kind: 'video', id: v.id });
  }
  for (const w of layout.webpages ?? []) {
    if (w.groupId === groupId) out.push({ kind: 'webpage', id: w.id });
  }
  for (const q of layout.quizzes) {
    if (q.groupId === groupId) out.push({ kind: 'quiz', id: q.id });
  }
  for (const o of layout.quizChoiceOptions) {
    if (o.groupId === groupId) out.push({ kind: 'quizChoice', id: o.id });
  }
  return out;
}

/** 선택된 블록 + 같은 `groupId`를 가진 동료 멤버를 합집합으로 펼침(중첩 그룹 없음). */
export function expandSlideRefsWithCoMembers(
  layout: ProgramSlideLayoutV1,
  refs: ReadonlyArray<{ kind: string; id: string }>
): SlideBlockGroupRef[] {
  const byKey = new Map<string, SlideBlockGroupRef>();
  const add = (r: SlideBlockGroupRef) => byKey.set(slideBlockGroupRefKey(r), r);
  refs.forEach((r) => add(r as SlideBlockGroupRef));
  const gids = new Set<string>();
  refs.forEach((r) => {
    const g = getSlideBlockGroupId(layout, r.kind, r.id);
    if (g) gids.add(g);
  });
  gids.forEach((gid) => {
    collectRefsWithGroupId(layout, gid).forEach(add);
  });
  return Array.from(byKey.values());
}

function stripGroupId<T extends { groupId?: string }>(b: T): T {
  if (b.groupId == null || b.groupId === '') return b;
  const { groupId: _g, ...rest } = b;
  return rest as T;
}

export function patchAssignGroupIdToRefs(
  layout: ProgramSlideLayoutV1,
  memberKeys: Set<string>,
  groupId: string
): ProgramSlideLayoutPatch {
  const hit = (kind: string, id: string) => memberKeys.has(`${kind}:${id}`);
  return {
    v: 1,
    boxes: layout.boxes.map((b) => (hit('text', b.id) ? { ...b, groupId } : b)),
    images: layout.images.map((im) => (hit('image', im.id) ? { ...im, groupId } : im)),
    clipboardImages: (layout.clipboardImages ?? []).map((im) =>
      hit('clipboardImage', im.id) ? { ...im, groupId } : im
    ),
    scanImages: (layout.scanImages ?? []).map((im) => (hit('scanImage', im.id) ? { ...im, groupId } : im)),
    buttons: layout.buttons.map((btn) => (hit('button', btn.id) ? { ...btn, groupId } : btn)),
    shapes: (layout.shapes ?? []).map((s) => (hit('shape', s.id) ? { ...s, groupId } : s)),
    polylines: (layout.polylines ?? []).map((pl) => (hit('polyline', pl.id) ? { ...pl, groupId } : pl)),
    tables: (layout.tables ?? []).map((tbl) => (hit('table', tbl.id) ? { ...tbl, groupId } : tbl)),
    videos: layout.videos.map((v) => (hit('video', v.id) ? { ...v, groupId } : v)),
    webpages: (layout.webpages ?? []).map((w) => (hit('webpage', w.id) ? { ...w, groupId } : w)),
    quizzes: layout.quizzes.map((q) => (hit('quiz', q.id) ? { ...q, groupId } : q)),
    quizChoiceOptions: layout.quizChoiceOptions.map((o) => (hit('quizChoice', o.id) ? { ...o, groupId } : o)),
  };
}

export function patchRemoveGroupIds(layout: ProgramSlideLayoutV1, groupIds: Set<string>): ProgramSlideLayoutPatch {
  const drop = (gid?: string) => gid != null && gid !== '' && groupIds.has(gid);
  return {
    v: 1,
    boxes: layout.boxes.map((b) => (drop(b.groupId) ? stripGroupId(b) : b)),
    images: layout.images.map((im) => (drop(im.groupId) ? stripGroupId(im) : im)),
    clipboardImages: (layout.clipboardImages ?? []).map((im) => (drop(im.groupId) ? stripGroupId(im) : im)),
    scanImages: (layout.scanImages ?? []).map((im) => (drop(im.groupId) ? stripGroupId(im) : im)),
    buttons: layout.buttons.map((btn) => (drop(btn.groupId) ? stripGroupId(btn) : btn)),
    shapes: (layout.shapes ?? []).map((s) => (drop(s.groupId) ? stripGroupId(s) : s)),
    polylines: (layout.polylines ?? []).map((pl) => (drop(pl.groupId) ? stripGroupId(pl) : pl)),
    tables: (layout.tables ?? []).map((tbl) => (drop(tbl.groupId) ? stripGroupId(tbl) : tbl)),
    videos: layout.videos.map((v) => (drop(v.groupId) ? stripGroupId(v) : v)),
    webpages: (layout.webpages ?? []).map((w) => (drop(w.groupId) ? stripGroupId(w) : w)),
    quizzes: layout.quizzes.map((q) => (drop(q.groupId) ? stripGroupId(q) : q)),
    quizChoiceOptions: layout.quizChoiceOptions.map((o) => (drop(o.groupId) ? stripGroupId(o) : o)),
  };
}
